import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import { MarkdownComponent } from "ngx-markdown";
import { OpenAI } from "openai";
import { DialogService } from "primeng/dynamicdialog";
import { FileInfo } from "src/app/common/components/file-content-list/file-content-list.component";
import { ImageDialogComponent } from "src/app/common/components/image-dialog/image-dialog.component";
import { FloatingAssistantChatService } from "src/app/common/services/floating-assistant-chat.service";
import { PillarboxService } from "src/app/common/services/pillarbox.service";
import { copyHtmlOfElement } from "src/app/common/tools/copyHtmlOfElement";

@Component({
  templateUrl: "./chat-line.component.html",
  selector: "app-chat-line",
  styleUrls: ["./chat-line.component.scss"],
})
export class ChatLineComponent implements OnInit {
  @Input({ required: true }) content!: OpenAI.ChatCompletionContentPart[] | string | null | undefined;
  @Input({ required: true }) username!: string;
  @Input({ required: false }) files?: FileInfo[];
  @Input({ required: false }) readonly = false;

  @Output() readonly delete = new EventEmitter<void>();
  @ViewChild(MarkdownComponent, { read: ElementRef }) markdownZone!: ElementRef;
  protected readonly floatingAssistantChatService = inject(FloatingAssistantChatService);
  protected readonly pillarboxService = inject(PillarboxService);
  protected readonly dialogService = inject(DialogService);
  protected readonly copyHtmlOfElement = copyHtmlOfElement;

  get usernameString(): string {
    switch (this.username) {
      case "assistant":
        return $localize`:@@chat_line_assistant_name:Assistant`;
      case "user":
        return $localize`:@@chat_line_user_name:Vous`;
      case "system":
        return $localize`:@@chat_line_system_name:Contexte`;
      default:
        return this.username;
    }
  }

  ngOnInit(): void {
    // Check if the content is a string and contains a timestamp as hh:mm:ss or mm:ss
    if (typeof this.content === "string") {
      const videoWithTimeRegex = /video(\d+) - (?:\d{1,2}:)?\d{1,2}:\d{1,2}/g;
      const hasMatch = videoWithTimeRegex.exec(this.content) !== null;
      this.content = this.content.replace(videoWithTimeRegex, (match) => {
        return `[${match}]()`;
      });
      if (!hasMatch) {
        const timeRegex = /(?:\d{1,2}:)?\d{1,2}:\d{2}/g;
        this.content = this.content.replace(timeRegex, (match) => {
          return `[${match}]()`;
        });
      }
    }
  }

  onMarkdownReady(): void {
    const nativeElement = this.markdownZone.nativeElement as HTMLElement;

    // Handle images: Make them clickable to open in a dialog
    const images = Array.from(nativeElement.querySelectorAll("img"));
    for (const img of images) {
      img.style.cursor = "pointer";
      img.addEventListener("click", (event: Event) => {
        event.preventDefault();
        const imageSrc = img.getAttribute("src");
        if (imageSrc) {
          this.openImageDialog(imageSrc);
        }
      });
    }

    // Find all links in the markdownZone as an array.
    const links = Array.from(nativeElement.querySelectorAll("a"));

    // Ensure `this.content` is a string.
    const contentAsString = typeof this.content === "string" ? this.content : "";

    // Extract the bullet-points section.
    const separatorRegex = /[=]{4,}/;
    const sections = contentAsString.split(separatorRegex);
    const bulletPointsSection = sections.length > 1 && sections[1] ? sections[1].trim() : contentAsString;

    // Use a for...of loop over the links.
    for (const link of links) {
      const href = link.getAttribute("href")?.trim();

      if (href) {
        // Build a regex that looks for a citation entry with this href.
        // Expected format: [Some text](<href>) [startTime - endTime] "Citation sentence"
        const escaped = href.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(
          `\\[[^\\]]+\\]\\(${escaped}\\)\\s*\\[(\\d+\\.\\d+)\\s*-\\s*\\d+\\.\\d+\\]\\s*["“](.*?)["”]`,
        );

        const match = regex.exec(bulletPointsSection);
        if (match?.[1] !== undefined && match[2] !== undefined) {
          const timecode = match[1].trim();
          const sentence = match[2].trim();
          link.dataset["timecode"] = timecode;
          link.dataset["sentence"] = sentence;
        } else {
          // If there is sentence or timecode set defaults.
          link.dataset["timecode"] = "0.00";
          link.dataset["sentence"] = link.innerHTML;
        }
      } else {
        // If there is no href, set defaults.
        link.dataset["timecode"] = "0.00";
        link.dataset["sentence"] = link.innerHTML;
      }

      // Add click event listener.
      link.addEventListener("click", (event: Event) => {
        event.preventDefault();
        void this.handleLinkClick(link);

        // If href points to an external origin, open in new tab
        if (href) {
          try {
            const url = new URL(href, window.location.href);
            if (url.origin !== window.location.origin) {
              window.open(url.toString(), "_blank");
            }
          } catch {
            // Invalid href, do nothing
          }
        }
      });
    }
  }

  private openImageDialog(imageUrl: string): void {
    this.dialogService.open(ImageDialogComponent, {
      data: {
        imageUrl,
      },
      header: $localize`:@@chat_line_image_preview_header:Prévisualisation de l'image`,
      width: "auto",
      modal: true,
      dismissableMask: true,
      contentStyle: { overflow: "hidden" },
      baseZIndex: 10000,
    });
  }

  private async handleLinkClick(link: HTMLAnchorElement): Promise<void> {
    await this.floatingAssistantChatService.onLinkClick(link);
  }

  extractMetadata(content: string): Map<string, [string, string]> {
    const result = new Map<string, [string, string]>();

    const normalize = (text: string): string => {
      return text
        .replace(/[\r\n]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    };

    // Matches "====" or more consecutive "=" characters as a separator
    const separatorRegex = /[=]{4,}/;
    const sections = content.split(separatorRegex);
    const mainContent = sections[0]?.trim();
    let bulletPointsSection = mainContent;
    if (sections.length > 1) {
      bulletPointsSection = sections[1]?.trim();
    }

    if (!mainContent || !bulletPointsSection) {
      return result;
    }

    // Updated regex: capture the citation link text (group 1), link id (group 2),
    // start timecode (group 3), and the citation sentence (group 4)
    // Example match:
    // [Le conseiller fédéral ...](15052729) [73.37 - 106.19] "Il va renforcer l'image de la Suisse..."
    const citationRegex = /\[(.*?)\]\((.*?)\)\s*\[(\d+\.\d+)\s*-\s*\d+\.\d+\]\s*["“](.*?)["”]/gm;
    let match;
    while ((match = citationRegex.exec(bulletPointsSection)) !== null) {
      if (match[1] && match[3] && match[4]) {
        const linkText = match[1].trim();
        const timecode = match[3].trim();
        const sentence = normalize(match[4]);
        result.set(linkText, [sentence, timecode]);
      }
    }

    return result;
  }
}
